import React, { useEffect, useMemo, useState } from 'react';
import { facebookOAuthAPI, platformPagesAPI } from '../../lib/api';

const PLATFORMS = [
  { value: 'telegram', label: 'تيليجرام' },
  { value: 'whatsapp', label: 'واتساب' },
  { value: 'facebook', label: 'فيسبوك ماسنجر' },
  { value: 'instagram', label: 'إنستغرام' },
  { value: 'web', label: 'ويب' },
  { value: 'other', label: 'أخرى' }
];

const emptyFormData = {
  name: '',
  platform: 'telegram',
  platformPageId: '',
  phoneNumberId: '',
  businessAccountId: '',
  credentials: {
    botToken: '',
    pageAccessToken: '',
    appSecret: '',
    verifyToken: ''
  },
  isActive: true
  
};

const formatPlatformName = (p) => PLATFORMS.find((x) => x.value === p)?.label || p;

export default function PlatformPagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [formErrors, setFormErrors] = useState({});
  const [facebookConnecting, setFacebookConnecting] = useState(false);

  const loadPages = async () => {
    setLoading(true);
    setError('');
    try {
      const params = filterPlatform !== 'all' ? { platform: filterPlatform } : {};
      const res = await platformPagesAPI.getAll(params);
      setPages(res.data?.pages || []);
    } catch (_err) {
      setError('فشل تحميل صفحات المنصات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, [filterPlatform]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('facebook_oauth_success');
    const oauthError = params.get('facebook_oauth_error');
    if (success === 'true') {
      facebookOAuthAPI.syncPages()
        .then((r) => {
          const { created, updated } = r.data || {};
          if (created > 0 || updated > 0) {
            alert(`تم المزامنة: ${created || 0} جديد، ${updated || 0} محدّث.`);
          }
          loadPages();
        })
        .catch((e) => alert(e.response?.data?.message || 'فشلت المزامنة'))
        .finally(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    } else if (oauthError === 'true') {
      alert('فشل الاتصال بفيسبوك/إنستغرام. يرجى المحاولة مرة أخرى.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleOpenModal = (page = null) => {
    if (page) {
      setEditingPage(page._id);
      setFormData({
        name: page.name || '',
        platform: page.platform || 'telegram',
        platformPageId: page.platformPageId || '',
        phoneNumberId: page.phoneNumberId || '',
        businessAccountId: page.businessAccountId || '',
        credentials: {
          botToken: page.credentials?.botToken || page.credentials?.token || '',
          pageAccessToken: page.credentials?.pageAccessToken || page.credentials?.accessToken || '',
          appSecret: page.credentials?.appSecret || '',
          verifyToken: page.credentials?.verifyToken || ''
        },
        isActive: page.isActive !== false
      });
    } else {
      setEditingPage(null);
      setFormData({ ...emptyFormData });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPage(null);
    setFormData({ ...emptyFormData });
    setFormErrors({});
  };

  const validateForm = () => {
    const err = {};
    if (!formData.name.trim()) err.name = 'الاسم مطلوب';
    if (formData.platform === 'whatsapp') {
      if (!formData.phoneNumberId?.trim()) err.phoneNumberId = 'معرّف رقم الهاتف مطلوب';
      if (!formData.businessAccountId?.trim()) err.businessAccountId = 'معرّف حساب الأعمال مطلوب';
      if (!formData.credentials.pageAccessToken?.trim()) err.credentials = 'رمز الوصول للصفحة مطلوب';
      if (!formData.credentials.verifyToken?.trim()) err.credentials = err.credentials || 'رمز التحقق مطلوب';
    } else if (!['facebook', 'instagram'].includes(formData.platform)) {
      if (!formData.platformPageId?.trim()) err.platformPageId = 'معرّف الصفحة مطلوب';
    }
    if (formData.platform === 'telegram' && !formData.credentials.botToken?.trim()) {
      err.credentials = 'رمز البوت مطلوب';
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (['facebook', 'instagram'].includes(formData.platform) && !editingPage) return;
    if (!validateForm()) return;

    const credentials = {};
    if (formData.platform === 'telegram') {
      credentials.botToken = formData.credentials.botToken;
      credentials.token = formData.credentials.botToken;
    } else if (['facebook', 'whatsapp'].includes(formData.platform)) {
      credentials.pageAccessToken = formData.credentials.pageAccessToken;
      credentials.accessToken = formData.credentials.pageAccessToken;
      if (formData.credentials.appSecret) credentials.appSecret = formData.credentials.appSecret;
      if (formData.credentials.verifyToken) credentials.verifyToken = formData.credentials.verifyToken;
    }

    try {
      if (editingPage) {
        const updatePayload = {
          name: formData.name,
          isActive: formData.isActive
        };
        if (!['facebook', 'instagram'].includes(formData.platform)) updatePayload.credentials = credentials;
        if (formData.platform === 'whatsapp') {
          updatePayload.phoneNumberId = formData.phoneNumberId;
          updatePayload.businessAccountId = formData.businessAccountId;
        }
        await platformPagesAPI.update(editingPage, updatePayload);
      } else {
        const createPayload = {
          name: formData.name,
          platform: formData.platform,
          credentials
        };
        if (formData.platform === 'whatsapp') {
          createPayload.phoneNumberId = formData.phoneNumberId;
          createPayload.businessAccountId = formData.businessAccountId;
        } else {
          createPayload.platformPageId = formData.platformPageId;
        }
        await platformPagesAPI.create(createPayload);
      }
      handleCloseModal();
      loadPages();
    } catch (err) {
      setFormErrors({ submit: err.response?.data?.message || 'فشل الحفظ' });
    }
  };

  const handleConnectFacebook = async () => {
    try {
      setFacebookConnecting(true);
      const res = await facebookOAuthAPI.getOAuthUrl();
      const url = res.data?.data?.url ?? res.data?.oauthUrl;
      if (url) window.location.href = url;
      else alert('لم يتم إرجاع رابط OAuth.');
    } catch (err) {
      alert(err.response?.data?.message || 'فشل الاتصال');
    } finally {
      setFacebookConnecting(false);
    }
  };

  const togglePage = async (id) => {
    await platformPagesAPI.toggleStatus(id);
    loadPages();
  };

  const removePage = async (id) => {
    if (!window.confirm('حذف صفحة المنصة هذه؟')) return;
    await platformPagesAPI.delete(id);
    loadPages();
  };

  const syncFacebookPages = async () => {
    await facebookOAuthAPI.syncPages();
    loadPages();
  };

  const filteredPages = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return pages;
    return pages.filter((p) =>
      [p.name, p.platform, p.platformPageId].some((f) => String(f || '').toLowerCase().includes(needle))
    );
  }, [pages, query]);

  const totalPages = pages.length;
  const activePages = pages.filter((p) => p.isActive).length;
  const inactivePages = totalPages - activePages;
  const byPlatform = pages.reduce((acc, p) => {
    const plat = p.platform || 'other';
    acc[plat] = (acc[plat] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 overflow-auto">
      <div className="p-4 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-[22px] font-semibold text-dark_text_1">صفحات المنصات</h2>
            <p className="text-sm text-dark_text_2 mt-1">إدارة قنوات المراسلة المتصلة</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={loadPages} className="px-3 py-1.5 rounded-lg bg-dark_bg_3 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm font-medium">
              تحديث
            </button>
            <button onClick={() => handleOpenModal()} className="px-3 py-1.5 rounded-lg bg-green_1 hover:bg-green_2 text-white text-sm font-medium">
              إضافة صفحة
            </button>
            <button onClick={handleConnectFacebook} disabled={facebookConnecting} className="px-3 py-1.5 rounded-lg bg-dark_bg_3 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm font-medium disabled:opacity-60">
              {facebookConnecting ? 'جاري الاتصال…' : 'اتصال فيسبوك/إنستغرام'}
            </button>
            <button onClick={syncFacebookPages} className="px-3 py-1.5 rounded-lg bg-dark_bg_3 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm font-medium">
              مزامنة
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">إجمالي الصفحات</p>
            <p className="mt-2 text-3xl text-dark_text_1 font-semibold">{totalPages}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">النشطة</p>
            <p className="mt-2 text-3xl text-emerald-300 font-semibold">{activePages}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">غير النشطة</p>
            <p className="mt-2 text-3xl text-amber-300 font-semibold">{inactivePages}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">حسب المنصة</p>
            <p className="mt-2 text-sm text-dark_text_1 truncate">
              {Object.entries(byPlatform).length === 0
                ? 'N/A'
                : Object.entries(byPlatform).map(([plat, n]) => `${formatPlatformName(plat)}: ${n}`).join(' | ')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            className="input w-auto min-w-[160px] py-1.5"
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
          >
            <option value="all">جميع المنصات</option>
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <input
            className="input flex-1 min-w-[180px] max-w-[280px] py-1.5"
            placeholder="البحث بالاسم أو المنصة أو المعرّف"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="text-xs text-dark_text_2">عرض {filteredPages.length} من {totalPages}</span>
        </div>

        <div className="card overflow-hidden">
          {loading ? <p className="p-4 text-dark_text_2">جاري التحميل...</p> : error ? <p className="p-4 text-red-400">{error}</p> : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="dark:bg-dark_bg_3">
                <tr>
                  <th className="text-start p-3 text-dark_text_2">الاسم</th>
                  <th className="text-start p-3 text-dark_text_2">المنصة</th>
                  <th className="text-start p-3 text-dark_text_2">معرّف الصفحة</th>
                  <th className="text-start p-3 text-dark_text_2">الحالة</th>
                  <th className="text-start p-3 text-dark_text_2">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredPages.map((page) => (
                  <tr key={page._id} className="border-t dark:border-dark_border_1">
                    <td className="p-3 text-dark_text_1 font-medium">{page.name}</td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-dark_bg_4 text-dark_text_1">{formatPlatformName(page.platform)}</span>
                    </td>
                    <td className="p-3 text-dark_text_2 font-mono text-xs">
                      {page.platform === 'whatsapp' ? (
                        <span title={`Phone: ${page.phoneNumberId}\nBusiness: ${page.businessAccountId}`}>
                          {page.phoneNumberId || page.platformPageId || '—'}
                        </span>
                      ) : (page.platform === 'facebook' || page.platform === 'instagram') ? (
                        <span>
                          {page.platformPageId || '—'}
                          {page.platform === 'facebook' && page.instagramAccount?.username && (
                            <span className="block mt-0.5 text-dark_text_1">@{page.instagramAccount.username}</span>
                          )}
                        </span>
                      ) : (
                        page.platformPageId || '—'
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${page.isActive ? 'bg-green_3 text-dark_text_1' : 'bg-dark_bg_4 text-dark_text_2'}`}>
                        {page.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleOpenModal(page)} className="px-2 py-1 rounded text-sm font-medium bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1">
                          تعديل
                        </button>
                        <button onClick={() => togglePage(page._id)} className="px-2 py-1 rounded text-sm font-medium bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1">
                          {page.isActive ? 'تعطيل' : 'تفعيل'}
                        </button>
                        <button onClick={() => removePage(page._id)} className="px-2 py-1 rounded text-sm font-medium bg-red-600 hover:bg-red-500 text-white">
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredPages.length === 0 && (
                  <tr className="border-t dark:border-dark_border_1">
                    <td className="p-6 text-center text-dark_text_2" colSpan={5}>لا توجد صفحات تطابق البحث.</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg dark:bg-dark_bg_2 border border-dark_border_1 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-dark_border_1 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-dark_text_1">{editingPage ? 'تعديل الصفحة' : 'إضافة صفحة'}</h3>
              <button onClick={handleCloseModal} className="w-8 h-8 rounded-lg hover:bg-dark_hover_1 flex items-center justify-center text-dark_text_2">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {formErrors.submit && (
                <div className="p-3 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">{formErrors.submit}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-dark_text_2 mb-1">الاسم *</label>
                <input
                  className={`input py-1.5 ${formErrors.name ? 'border-red-500' : ''}`}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="مثال: بوت المبيعات الرئيسي"
                />
                {formErrors.name && <p className="text-xs text-red-400 mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-dark_text_2 mb-1">المنصة *</label>
                <select
                  className={`input py-1.5 ${formErrors.platform ? 'border-red-500' : ''}`}
                  value={formData.platform}
                  onChange={(v) => {
                    const val = v.target.value;
                    setFormData({
                      ...emptyFormData,
                      name: formData.name,
                      platform: val,
                      credentials: {}
                    });
                  }}
                  disabled={!!editingPage}
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {formData.platform === 'instagram' && (
                <div className="p-3 rounded-lg bg-dark_bg_3 border border-dark_border_1">
                  <p className="text-sm text-dark_text_2 mb-3">صفحات إنستغرام تُضاف عبر مزامنة فيسبوك. اتصل بحساب فيسبوك لمزامنة حسابات إنستغرام المرتبطة.</p>
                  <button type="button" onClick={handleConnectFacebook} disabled={facebookConnecting} className="w-full py-2 rounded-lg bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm">
                    {facebookConnecting ? 'جاري الاتصال…' : 'اتصال فيسبوك وإنستغرام'}
                  </button>
                </div>
              )}

              {formData.platform === 'facebook' && (
                <div className="p-3 rounded-lg bg-dark_bg_3 border border-dark_border_1">
                  {!editingPage ? (
                    <>
                      <p className="text-sm text-dark_text_2 mb-3">اتصل عبر TijaraPlus لإضافة صفحات فيسبوك وإنستغرام المرتبطة.</p>
                      <button type="button" onClick={handleConnectFacebook} disabled={facebookConnecting} className="w-full py-2 rounded-lg bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm">
                        {facebookConnecting ? 'جاري الاتصال…' : 'اتصال فيسبوك وإنستغرام'}
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-dark_text_2">فيسبوك وإنستغرام يُداران عبر TijaraPlus. استخدم الزر أعلاه لإعادة المزامنة.</p>
                  )}
                </div>
              )}

              {formData.platform === 'whatsapp' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-dark_text_2 mb-1">معرّف رقم الهاتف *</label>
                    <input
                      className={`input py-1.5 ${formErrors.phoneNumberId ? 'border-red-500' : ''}`}
                      value={formData.phoneNumberId}
                      onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
                      placeholder="مثال: 123456789012345"
                    />
                    {formErrors.phoneNumberId && <p className="text-xs text-red-400 mt-1">{formErrors.phoneNumberId}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark_text_2 mb-1">معرّف حساب الأعمال *</label>
                    <input
                      className={`input py-1.5 ${formErrors.businessAccountId ? 'border-red-500' : ''}`}
                      value={formData.businessAccountId}
                      onChange={(e) => setFormData({ ...formData, businessAccountId: e.target.value })}
                      placeholder="مثال: 987654321098765"
                    />
                    {formErrors.businessAccountId && <p className="text-xs text-red-400 mt-1">{formErrors.businessAccountId}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark_text_2 mb-1">رمز الوصول للصفحة *</label>
                    <input
                      type="password"
                      className={`input py-1.5 ${formErrors.credentials ? 'border-red-500' : ''}`}
                      value={formData.credentials.pageAccessToken}
                      onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, pageAccessToken: e.target.value } })}
                      placeholder="EAA..."
                    />
                    {formErrors.credentials && <p className="text-xs text-red-400 mt-1">{formErrors.credentials}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark_text_2 mb-1">رمز التحقق *</label>
                    <input
                      className={`input py-1.5`}
                      value={formData.credentials.verifyToken}
                      onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, verifyToken: e.target.value } })}
                      placeholder="my_verify_token"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark_text_2 mb-1">سر التطبيق (اختياري)</label>
                    <input
                      type="password"
                      className="input py-1.5"
                      value={formData.credentials.appSecret}
                      onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, appSecret: e.target.value } })}
                      placeholder="abc123..."
                    />
                  </div>
                </>
              )}

              {!['facebook', 'instagram', 'whatsapp'].includes(formData.platform) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-dark_text_2 mb-1">معرّف الصفحة *</label>
                    <input
                      className={`input py-1.5 ${formErrors.platformPageId ? 'border-red-500' : ''}`}
                      value={formData.platformPageId}
                      onChange={(e) => setFormData({ ...formData, platformPageId: e.target.value })}
                      placeholder={formData.platform === 'telegram' ? 'اسم البوت (مثال: my_bot)' : 'معرّف الصفحة'}
                      disabled={!!editingPage}
                    />
                    {formErrors.platformPageId && <p className="text-xs text-red-400 mt-1">{formErrors.platformPageId}</p>}
                  </div>
                  {formData.platform === 'telegram' && (
                    <div>
                      <label className="block text-sm font-medium text-dark_text_2 mb-1">رمز البوت *</label>
                      <input
                        type="password"
                        className={`input py-1.5 ${formErrors.credentials ? 'border-red-500' : ''}`}
                        value={formData.credentials.botToken}
                        onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, botToken: e.target.value } })}
                        placeholder="123456:ABC-DEF..."
                      />
                      {formErrors.credentials && <p className="text-xs text-red-400 mt-1">{formErrors.credentials}</p>}
                    </div>
                  )}
                </>
              )}

              {editingPage && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-dark_border_1"
                  />
                  <label htmlFor="isActive" className="text-sm text-dark_text_2">الصفحة نشطة</label>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {!['facebook', 'instagram'].includes(formData.platform) && (
                  <button type="submit" className="flex-1 py-2 rounded-lg bg-green_1 hover:bg-green_2 text-white text-sm font-medium">
                    {editingPage ? 'حفظ' : 'إنشاء'}
                  </button>
                )}
                <button type="button" onClick={handleCloseModal} className="flex-1 py-2 rounded-lg bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
